import { apiClient } from '../api/axiosInstance';
import { cachedFetch } from '../api/apiCache';
import type { Provider } from '../types';

/**
 * The provider directory read, behind an entity-shaped result so the source stays an
 * implementation detail of this module.
 *
 * tenant-service serves the directory entirely from storage, so an upstream outage can neither
 * empty this list nor fail this call. Cached (`cachedFetch`) because a picker mounts on several
 * pages and the directory changes at most once a night.
 */

/** Wire shape of one provider in a practice's directory. */
interface ProviderWire {
  id: string;
  npi: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  credential?: string;
  isActive: boolean;
}

function toProvider(p: ProviderWire): Provider {
  return { id: p.id, name: p.displayName, npi: p.npi, credential: p.credential };
}

export function getProviders(): Promise<Provider[]> {
  return cachedFetch('providers', async () => {
    const response = await apiClient.get<ProviderWire[]>('/api/v1/tenants/providers');
    return response.data.map(toProvider);
  });
}

/**
 * Cross-tenant provider list for the back office. Unlike {@link getProviders} — whose practice
 * comes from the JWT, and which declares no parameter that could name another one — the back
 * office is cross-tenant and passes the encounter's tenant id explicitly. Cached per tenant.
 */
export function getProvidersByTenant(tenantId: string): Promise<Provider[]> {
  return cachedFetch(`providers:${tenantId}`, async () => {
    const response = await apiClient.get<ProviderWire[]>('/api/v1/tenants/providers', {
      params: { tenantId },
    });
    return response.data.map(toProvider);
  });
}
